import logger from '@ayocore/platform/common/helpers/logger';
import {
  activityMentionRegistry,
  type ActivityContactSearchItem,
  type ActivityMentionThreadContext,
} from '@ayocore/activity/client/extensions';
import { useDebouncedSearchWithCache } from './useDebouncedSearchWithCache';

type UseActivityContactSearchParams = {
  enabled: boolean;
  query: string;
  threadEntityType?: string | null;
  threadEntityUuid?: string | null;
};

type UseActivityContactSearchResult = {
  contactItems: ActivityContactSearchItem[];
  contactsLoading: boolean;
};

/**
 * Поиск контактных лиц для inline-picker'а через реестр провайдеров Activity.
 *
 * Активный провайдер — первый с `isAvailable(ctx)`; без провайдера picker
 * возвращает пустую выдачу (graceful fallback).
 */
export function useActivityContactSearch({
  enabled,
  query,
  threadEntityType,
  threadEntityUuid,
}: UseActivityContactSearchParams): UseActivityContactSearchResult {
  const ctx: ActivityMentionThreadContext = { threadEntityType, threadEntityUuid };
  const provider = activityMentionRegistry.resolveContactProvider(ctx);
  const normalizedQuery = query.trim().toLowerCase();

  const { results, loading } = useDebouncedSearchWithCache<ActivityContactSearchItem>({
    enabled: enabled && provider != null,
    cacheKey: `${provider?.id ?? 'none'}::${threadEntityType ?? ''}::${threadEntityUuid ?? ''}::${normalizedQuery}`,
    fetcher: async () => (provider ? provider.search(normalizedQuery, ctx) : []),
    onError: error => {
      logger.warn('[useActivityContactSearch] Contact search failed', {
        provider_id: provider?.id,
        query: normalizedQuery,
        threadEntityType,
        threadEntityUuid,
        error,
      });
    },
  });

  return {
    contactItems: results,
    contactsLoading: loading,
  };
}
